import type { Breadcrumb, BrowserOptions, CaptureContext, ErrorEvent } from '@sentry/react';
import { addBreadcrumb, captureException, captureMessage, reactErrorHandler, replayIntegration, setContext } from '@sentry/react';

type TransactionEvent = Parameters<NonNullable<BrowserOptions['beforeSendTransaction']>>[0];

/** Browser noise that says nothing about our code. Matched against the error message. */
export const COMMON_IGNORE_ERRORS: ReadonlyArray<string | RegExp> = [
  // Benign layout notification; every browser fires it, nothing is broken.
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications.',
  // Connectivity loss on the user's side. A real outage shows up on the backend, where it can be acted on.
  /^(?:Failed to fetch|Load failed|NetworkError when attempting to fetch resource\.?|Network Error)$/,
];

/** Errors thrown by browser extensions, not by us. */
export const COMMON_DENY_URLS: ReadonlyArray<RegExp> = [
  /^chrome-extension:\/\//i,
  /^moz-extension:\/\//i,
  /^safari-(?:web-)?extension:\/\//i,
];

/** Same-origin relative requests, which Sentry stops propagating to once `tracePropagationTargets` is customised. */
const SAME_ORIGIN_PATHS = /^\/(?!\/)/;

/** Drops the query string and fragment, which can carry tokens, ids or other personal data. */
export function scrubUrl(url: string): string {
  const end = url.search(/[?#]/);
  return end === -1 ? url : url.slice(0, end);
}

/**
 * Where trace headers may be sent: same-origin paths plus the origins of our own APIs. Without this Sentry only
 * propagates to the page's own origin, and the headers would never reach a separately hosted API (or, with a
 * broad pattern, a third party).
 */
export function buildTracePropagationTargets(apiUrls: ReadonlyArray<string | undefined>): Array<string | RegExp> {
  const origins = new Set<string>();
  for (const apiUrl of apiUrls) {
    if (!apiUrl) continue;
    try {
      origins.add(new URL(apiUrl).origin);
    } catch {
      // Not an absolute URL (e.g. a relative base path): same-origin propagation already covers it.
    }
  }
  const originTargets = [...origins].map((origin) => {
    const escapedOrigin = origin.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp(`^${escapedOrigin}(?:[/?#]|$)`);
  });
  return [SAME_ORIGIN_PATHS, ...originTargets];
}

/** Strips query strings and fragments from the URLs a breadcrumb records. */
export function scrubBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb {
  const data = breadcrumb.data;
  if (!data) return breadcrumb;

  for (const key of ['url', 'from', 'to']) {
    const value = data[key];
    if (typeof value === 'string') data[key] = scrubUrl(value);
  }
  return breadcrumb;
}

function scrubRequest(event: ErrorEvent | TransactionEvent): void {
  if (!event.request) return;
  if (event.request.url) event.request.url = scrubUrl(event.request.url);
  delete event.request.query_string;
  delete event.request.cookies;
}

function scrubTransaction(event: TransactionEvent): TransactionEvent {
  scrubRequest(event);
  for (const span of event.spans ?? []) {
    if (span.description) span.description = span.description.replace(/[?#][^\s]*/, '');
    const data = span.data;
    if (!data) continue;
    for (const key of ['http.url', 'url.full', 'url']) {
      const value = data[key];
      if (typeof value === 'string') data[key] = scrubUrl(value);
    }
    for (const key of ['http.query', 'url.query', 'http.fragment', 'url.fragment']) {
      delete data[key];
    }
  }
  return event;
}

export type SentryBaseOptions = {
  dsn: string | undefined;
  enabled: boolean;
  environment: string;
  release: string | undefined;
  /** Short app name, set as the `app` tag so one Sentry project can be filtered per frontend. */
  app: string;
  platform?: string;
  /** Fraction of page loads and navigations traced. Undefined turns tracing off. Also decides how often the backend is sampled. */
  tracesSampleRate: number | undefined;
  /** See {@link buildTracePropagationTargets}. Omit to keep Sentry's default (same-origin only). */
  tracePropagationTargets?: Array<string | RegExp>;
  /** Also send W3C `traceparent`; the APIs must allow that header in CORS first. */
  propagateTraceparent?: boolean;
};

/**
 * The init options every browser app shares: privacy defaults, noise filtering, URL scrubbing and sampling.
 * Apps add their own `integrations` (router instrumentation, replay) on top.
 */
export function createSentryBaseOptions(options: SentryBaseOptions): BrowserOptions {
  return {
    dsn: options.dsn,
    enabled: options.enabled,
    environment: options.environment,
    release: options.release,
    sampleRate: 1,
    tracesSampleRate: options.tracesSampleRate,
    tracePropagationTargets: options.tracePropagationTargets,
    propagateTraceparent: options.propagateTraceparent,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    maxValueLength: 1000,
    sendDefaultPii: false,
    ignoreErrors: [...COMMON_IGNORE_ERRORS],
    denyUrls: [...COMMON_DENY_URLS],
    beforeBreadcrumb: scrubBreadcrumb,
    beforeSend: (event) => {
      scrubRequest(event);
      return event;
    },
    beforeSendTransaction: scrubTransaction,
    initialScope: (scope) => {
      scope.setTag('app', options.app);
      if (options.platform) scope.setTag('platform', options.platform);
      return scope;
    },
  };
}

/**
 * Session replay with everything masked: all text and inputs, and media blocked. Replays record the DOM, and these
 * apps show customer data, so unmasking should be a deliberate, per-element choice (`data-sentry-unmask`).
 */
export const createMaskedReplayIntegration = (): ReturnType<typeof replayIntegration> =>
  replayIntegration({ maskAllText: true, maskAllInputs: true, blockAllMedia: true });

/**
 * React 19 `createRoot` options that report errors React handles itself. Uncaught errors and recoverable errors
 * only: errors caught by a Sentry `ErrorBoundary` are already captured there.
 */
export const createSentryRootOptions = () => ({
  onUncaughtError: reactErrorHandler(),
  onRecoverableError: reactErrorHandler(),
});

type LoggerValue = Error | string | number | boolean | bigint | object | null | undefined;

type LoggerErrorContext = {
  tags?: Record<string, string | number | boolean>;
  extra?: Record<string, LoggerValue>;
  level?: 'fatal' | 'error' | 'warning' | 'info' | 'debug';
  fingerprint?: string[];
  contexts?: Record<string, LoggerValue>;
};

/**
 * Sentry behind the error-reporting shape `TelemetryLogger` (`@trinity/observability-web`) defines. Its calls do
 * nothing unless Sentry was initialized, so it is safe to use whether or not an environment enables Sentry.
 */
export const createSentryLogger = () => ({
  addBreadcrumb: (breadcrumb: { message?: string }): void => addBreadcrumb(breadcrumb),
  captureMessage: (message: string): void => {
    captureMessage(message);
  },
  captureException: (error: unknown, context?: LoggerErrorContext): void => {
    captureException(error, context as CaptureContext | undefined);
  },
  setContext: (name: string, context: Record<string, LoggerValue>): void => setContext(name, context),
});
